import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import {
    FILTER_CHIP_FOCUS_BG,
    FILTER_CHIP_SELECT_CLASS,
    filterChipLabelStyle,
    getFilterChipShellStyle,
    getFilterChipValueSelectStyle,
    handleFilterChipValueSelectBlur,
    handleFilterChipValueSelectFocus,
    measureValueSelectWidth,
} from "./filterChipStyles.js"

const DROPDOWN_PLACEHOLDER_VALUE = ""
const DROPDOWN_PLACEHOLDER_LABEL = "Select..."

function getOptionDisplayText(value, options) {
    if (!value) {
        return DROPDOWN_PLACEHOLDER_LABEL
    }
    const selectedOption = options.find((option) => option.value === value && !option.disabled)
    return selectedOption ? selectedOption.display : value
}

const DropdownFilterChip = forwardRef(({
    label = "",
    displayLabel = "",
    value = "",
    options = [],
    isHighlighted = false,
    onValueChange = () => {},
}, ref) => {
    const [currentValue, setCurrentValue] = useState(value ?? "")
    const [dropdownWidth, setDropdownWidth] = useState(20)
    const [isPickerOpen, setIsPickerOpen] = useState(false)
    const measureRef = useRef(null)
    const selectRef = useRef(/** @type {HTMLSelectElement | null} */(null))

    useEffect(() => {
        setCurrentValue(value ?? "")
    }, [value, options])

    useEffect(() => {
        const displayText = getOptionDisplayText(value, options)
        setDropdownWidth(measureValueSelectWidth(measureRef.current, displayText))
    }, [value, options])

    function handleValueChange(newValue) {
        const selectedOption = options.find((option) => option.value === newValue)
        if (selectedOption?.disabled) {
            return
        }
        if (newValue === currentValue) {
            return
        }
        setCurrentValue(newValue)
        const displayText = getOptionDisplayText(newValue, options)
        setDropdownWidth(measureValueSelectWidth(measureRef.current, displayText))
        onValueChange?.(newValue)
    }

    function openDropdown() {
        const selectEl = selectRef.current
        if (!selectEl) {
            return
        }
        setIsPickerOpen(true)
        selectEl.focus()
        if (typeof selectEl.showPicker === "function") {
            try {
                selectEl.showPicker()
            } catch {
                selectEl.click()
            }
        } else {
            selectEl.click()
        }
    }

    function handleSelectBlur() {
        setIsPickerOpen(false)
    }

    useImperativeHandle(ref, () => ({
        getSearchString: () => `{${label} = ${currentValue}}`,
        getChipState: () => ({
            label,
            command: "=",
            value: currentValue,
        }),
        openDropdown,
    }))

    const valueSelectStyle = {
        ...getFilterChipValueSelectStyle(dropdownWidth),
        ...(isPickerOpen ? { background: FILTER_CHIP_FOCUS_BG } : {}),
    }

    const shellStyle = {
        ...getFilterChipShellStyle(isHighlighted),
        margin: 0,
        cursor: "pointer",
    }

    return (
        <label
            className="flex-shrink-0"
            style={shellStyle}
            onMouseDown={(event) => event.stopPropagation()}
        >
            <span style={filterChipLabelStyle}>{displayLabel || label}</span>
            <select
                ref={selectRef}
                className={FILTER_CHIP_SELECT_CLASS}
                value={currentValue}
                onChange={(event) => handleValueChange(event.target.value)}
                tabIndex={-1}
                style={valueSelectStyle}
                onFocus={(event) => {
                    setIsPickerOpen(true)
                    handleFilterChipValueSelectFocus(event)
                }}
                onBlur={(event) => {
                    handleSelectBlur()
                    handleFilterChipValueSelectBlur(event)
                }}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <option value={DROPDOWN_PLACEHOLDER_VALUE}>{DROPDOWN_PLACEHOLDER_LABEL}</option>
                {options.map((option, index) => (
                    <option
                        key={index}
                        value={option.value}
                        disabled={option.disabled}
                    >
                        {option.display}
                    </option>
                ))}
            </select>
            <span
                ref={measureRef}
                style={{
                    position: "absolute",
                    visibility: "hidden",
                    whiteSpace: "nowrap",
                    fontFamily: "inherit",
                    pointerEvents: "none",
                }}
            />
        </label>
    )
})

DropdownFilterChip.displayName = "DropdownFilterChip"

export default DropdownFilterChip
